<template>
  <nav
    class="relative hidden w-full xl:block border-b border-gray-200 bg-white/95 backdrop-blur supports-[backdrop-filter]:bg-white/90 dark:border-slate-700 dark:bg-slate-900/95 dark:supports-[backdrop-filter]:bg-slate-900/90 shadow-sm z-30 transition-colors duration-300"
  >
    <div class="mx-auto flex max-w-6xl items-start justify-between gap-4 px-8 py-4">
      <div class="flex items-start gap-8">
        <RouterLink :to="{ name: 'payment' }" aria-label="YouPark" class="inline-flex shrink-0 pt-0.5">
          <IconLogo class="h-8" />
        </RouterLink>

        <div class="flex items-start gap-6 text-sm font-semibold text-gray-700 dark:text-slate-200 pt-1">
          <template v-for="link in navItems" :key="link.key">
            <button
              v-if="link.view"
              type="button"
              class="relative whitespace-nowrap px-1 py-2 transition-all duration-200 hover:text-gray-900 dark:hover:text-white"
              :class="{ 'text-gray-900 dark:text-white font-bold': isActive(link) }"
              @click="handleLinkClick(link)"
            >
              {{ translate(link.labelKey) }}
              <span
                v-if="isActive(link)"
                class="absolute -bottom-[1.45rem] left-0 right-0 h-0.5 bg-emerald-500 dark:bg-emerald-400"
              ></span>
            </button>
            <RouterLink
              v-else-if="link.routeName"
              :to="{ name: link.routeName }"
              class="relative whitespace-nowrap px-1 py-2 transition-all duration-200 hover:text-gray-900 dark:hover:text-white"
              :class="{ 'text-gray-900 dark:text-white font-bold': isActive(link) }"
            >
              {{ translate(link.labelKey) }}
              <span
                v-if="isActive(link)"
                class="absolute -bottom-[1.45rem] left-0 right-0 h-0.5 bg-emerald-500 dark:bg-emerald-400"
              ></span>
            </RouterLink>
            <a
              v-else
              class="relative whitespace-nowrap px-1 py-2 transition-all duration-200 hover:text-gray-900 dark:hover:text-white"
              :href="link.href"
              :target="link.external ? '_blank' : undefined"
              :rel="link.external ? 'noreferrer' : undefined"
            >
              {{ translate(link.labelKey) }}
            </a>
          </template>
        </div>
      </div>

      <div class="flex items-center gap-3">
        <button
          v-if="!(isMapPage && config.isAuthenticated)"
          type="button"
          class="inline-flex items-center justify-center rounded-xl bg-emerald-600 dark:bg-emerald-500 px-4 py-2 text-sm font-semibold text-white transition-all duration-200 hover:bg-emerald-700 dark:hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 whitespace-nowrap"
          @click="handleSignInClick"
        >
          {{ translate("nav.registerOrSignIn") }}
        </button>
        <button
          v-else
          type="button"
          class="inline-flex items-center justify-center rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-2 text-sm font-semibold text-gray-700 dark:text-slate-200 transition-all duration-200 hover:bg-gray-50 dark:hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-300 dark:focus-visible:ring-slate-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 whitespace-nowrap"
          @click="handleSignOutClick"
        >
          {{ translate("nav.signOut") }}
        </button>
        <ThemeToggle />
        <div class="pl-1">
          <LanguageSwitcher compact dropdown-direction="left" />
        </div>
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed } from "vue";

import LanguageSwitcher from "@core/components/shared/LanguageSwitcher.vue";
import { useAppConfig } from "@core/composables/data/useAppConfig";
import { useNavigation, type ViewType } from "@core/composables/state/useNavigation";
import { useRouteContext } from "@core/composables/state/useRouteContext";
import IconLogo from "@core/icons/IconLogo.vue";

import { useTranslate } from "@common/composables/useTranslate";

import ThemeToggle from "../components/ThemeToggle.vue";

type InternalView = Extract<ViewType, "home" | "payment" | "faq" | "profile" | "vehicles" | "subscriptions">;
type NavRouteName = "map-parking" | "map-toll" | "map-area" | "map";

interface NavItem {
  key: string;
  labelKey: string;
  view?: InternalView;
  routeName?: NavRouteName;
  href?: string;
  external?: boolean;
}

const translate = useTranslate();
const { navigateTo, activeView } = useNavigation();
const { config } = useAppConfig();
const route = useRouteContext();

const isMapPage = computed(() => {
  return route.meta?.layout === "map";
});

const navItems = computed<NavItem[]>(() => {
  if (isMapPage.value && config.isAuthenticated) {
    return [
      { key: "home", labelKey: "nav.home", view: "home" },
      {
        key: "parking",
        labelKey: "nav.parkering",
        routeName: "map-parking"
      },
      {
        key: "tollRoads",
        labelKey: "nav.bomvei",
        routeName: "map-toll"
      },
      { key: "profile", labelKey: "nav.profile", view: "profile" },
      { key: "vehicles", labelKey: "nav.vehicles", view: "vehicles" },
      { key: "subscriptions", labelKey: "nav.subscriptions", view: "subscriptions" }
    ];
  }

  return [
    { key: "payment", labelKey: "nav.paymentQuick", view: "payment" },
    {
      key: "parking",
      labelKey: "nav.parkering",
      routeName: "map-parking"
    },
    {
      key: "tollRoads",
      labelKey: "nav.bomvei",
      routeName: "map-toll"
    },
    { key: "products", labelKey: "nav.products", href: "https://www.finter.no", external: true },
    { key: "faq", labelKey: "nav.faq", view: "faq" }
  ];
});

const isActive = (link: NavItem) => {
  if (link.view) {
    return activeView.value === link.view;
  }
  if (link.routeName) {
    if (link.routeName === "map-parking") {
      return activeView.value === "map-parking";
    }
    if (link.routeName === "map-toll") {
      return activeView.value === "map-toll";
    }
    return route.name === link.routeName;
  }
  return false;
};

const handleLinkClick = (link: NavItem) => {
  if (link.view) {
    void navigateTo(link.view);
    return;
  }
  if (link.href) {
    window.location.href = link.href;
  }
};

const handleSignInClick = () => {
  void navigateTo("sign-in");
};

const handleSignOutClick = async () => {
  const { signOut } = await import("@core/utils/auth");
  await signOut();
};
</script>
